import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@core-ui/Components/ui/button';
import { Badge } from '@core-ui/Components/ui/badge';

import { Pause, Play, Square, Users, Loader2, Phone } from 'lucide-react';

interface SessionHeaderProps {
  queueCount: number;
  phoneNumber?: string;
  isPaused: boolean;
  onPause: () => void;
  onResume: () => void;
  onEnd: () => void;
  isEnding?: boolean;
}

export default function SessionHeader ({
  queueCount,
  phoneNumber,
  isPaused,
  onPause,
  onResume,
  onEnd,
  isEnding = false,
}: SessionHeaderProps): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="flex flex-wrap items-center justify-between gap-4 rounded-md border border-border bg-card p-4">
      <div className="flex items-center gap-6">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-muted-foreground" />
          <span className="text-sm font-medium text-muted-foreground">
            {t('wizard.dashboard.queue')}:
          </span>
          <span className="text-sm font-semibold text-foreground">
            {queueCount}
          </span>
        </div>

        {phoneNumber !== undefined && phoneNumber !== '' && (
          <div className="flex items-center gap-2">
            <Phone className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium text-muted-foreground">
              {t('wizard.dashboard.callingFrom', 'Calling from')}:
            </span>
            <span className="text-sm font-mono font-semibold text-foreground">
              {phoneNumber}
            </span>
          </div>
        )}

        {isPaused && (
          <Badge
            variant="outline"
            className="bg-amber-500/10 text-amber-700 border-amber-500/30 dark:text-amber-300"
          >
            {t('wizard.dashboard.paused')}
          </Badge>
        )}
      </div>

      <div className="flex items-center gap-3">
        {isPaused ? (
          <Button
            variant="outline"
            size="sm"
            onClick={onResume}
            className="gap-2 border border-border text-foreground hover:bg-muted"
          >
            <Play className="h-4 w-4" />
            {t('wizard.dashboard.resume')}
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={onPause}
            className="gap-2 border border-border text-foreground hover:bg-muted"
          >
            <Pause className="h-4 w-4" />
            {t('wizard.dashboard.pause')}
          </Button>
        )}

        <Button
          variant="destructive"
          size="sm"
          onClick={onEnd}
          disabled={isEnding}
          className="gap-2"
        >
          {isEnding ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Square className="h-4 w-4" />
          )}
          {t('wizard.dashboard.endSession')}
        </Button>
      </div>
    </div>
  );
}
